import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '@/api/axios';
import { Button } from '@/components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import { cn } from '@/components/ui/utils';

const { FiSend, FiX, FiCheckCircle, FiAlertCircle, FiLoader, FiZap, FiInfo } = FiIcons;

const ResendBroadcastModal = ({ isOpen, onClose, schemeId, schemeName }) => {
  const [step, setStep] = useState('confirm'); // confirm, processing, result
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setStep('confirm');
      setResult(null);
      setError(null);
    }
  }, [isOpen]);

  const handleStartBroadcast = async () => {
    setStep('processing');
    setError(null);
    
    try {
      const res = await api.post('resend_broadcast.php', { scheme_id: schemeId });
      if (res.data?.status) {
        setResult(res.data);
        setStep('result');
      } else {
        throw new Error(res.data?.message || 'Failed to initiate broadcast');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An unexpected error occurred');
      setStep('confirm');
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-surface-950/60 backdrop-blur-sm">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }} 
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-[2.5rem] p-8 max-w-md w-full shadow-2xl border border-surface-200 relative overflow-hidden"
      >
        {/* Decorative background element */}
        <div className="absolute -top-24 -right-24 w-48 h-48 bg-primary-50 rounded-full blur-3xl opacity-50" />

        <div className="flex justify-between items-start mb-6 relative z-10">
          <div className={cn(
            "w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg",
            step === 'result' && result?.succeeded > 0 ? "bg-emerald-500 shadow-emerald-100" : "bg-primary-600 shadow-primary-100"
          )}>
            <SafeIcon icon={step === 'result' ? (result?.succeeded > 0 ? FiCheckCircle : FiAlertCircle) : FiSend} className="w-6 h-6 text-white" />
          </div>
          <button onClick={onClose} className="p-2 hover:bg-surface-100 rounded-xl transition-colors shrink-0">
            <SafeIcon icon={FiX} className="w-5 h-5 text-surface-400" />
          </button>
        </div>

        <div className="relative z-10">
          {step === 'confirm' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-black text-surface-900 leading-tight uppercase tracking-tight">Resend Broadcast</h3>
                <p className="text-sm text-surface-500 mt-2 font-medium">This will trigger a WhatsApp message via Pabbly to <span className="font-bold text-primary-600">all pending users</span> in the scheme: <span className="italic">"{schemeName}"</span>.</p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-100 p-4 rounded-2xl flex items-start gap-3">
                  <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-xs font-bold text-red-700 leading-relaxed">{error}</p>
                </div>
              )}

              <div className="bg-primary-50 rounded-2xl p-4 border border-primary-100 flex items-start gap-3">
                <SafeIcon icon={FiInfo} className="w-5 h-5 text-primary-500 shrink-0 mt-0.5" />
                <p className="text-[11px] font-bold text-primary-700 leading-relaxed uppercase tracking-tight">
                  System will automatically skip users who have completed the process or marked as "Not Shop Owner".
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <Button className="w-full h-14 rounded-2xl text-xs font-black uppercase tracking-[0.2em]" onClick={handleStartBroadcast}>
                  <SafeIcon icon={FiZap} className="w-4 h-4 mr-2" />
                  Initiate Broadcast
                </Button>
                <Button variant="ghost" className="w-full h-12 rounded-2xl font-bold text-surface-400" onClick={onClose}>
                  Cancel Action
                </Button>
              </div>
            </div>
          )}

          {step === 'processing' && (
            <div className="text-center py-10 space-y-6">
              <div className="relative w-20 h-20 mx-auto">
                <div className="absolute inset-0 rounded-full border-4 border-primary-50 animate-pulse" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <SafeIcon icon={FiLoader} className="w-10 h-10 text-primary-600 animate-spin" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-black text-surface-900 uppercase tracking-widest">Processing Batch</h3>
                <p className="text-xs text-surface-400 mt-2 font-bold italic">Sending messages via Pabbly API...</p>
              </div>
              <div className="px-6">
                <div className="h-1.5 w-full bg-surface-100 rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ x: '-100%' }} 
                    animate={{ x: '100%' }} 
                    transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
                    className="h-full w-1/2 bg-primary-600"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 'result' && result && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-xl font-black text-surface-900 uppercase tracking-tight">Broadcast Results</h3>
                <p className="text-xs text-surface-500 mt-1 font-bold">Batch processing summary</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-surface-50 p-4 rounded-2xl border border-surface-100 text-center">
                  <p className="text-[10px] font-black text-surface-400 uppercase tracking-widest mb-1">Processed</p>
                  <p className="text-2xl font-black text-surface-900">{result.total_processed}</p>
                </div>
                <div className="bg-emerald-50 p-4 rounded-2xl border border-emerald-100 text-center">
                  <p className="text-[10px] font-black text-emerald-500 uppercase tracking-widest mb-1">Succeeded</p>
                  <p className="text-2xl font-black text-emerald-600">{result.succeeded}</p>
                </div>
                <div className="bg-red-50 p-4 rounded-2xl border border-red-100 text-center">
                  <p className="text-[10px] font-black text-red-400 uppercase tracking-widest mb-1">Failed</p>
                  <p className="text-2xl font-black text-red-600">{result.failed}</p>
                </div>
                <div className="bg-amber-50 p-4 rounded-2xl border border-amber-100 text-center">
                  <p className="text-[10px] font-black text-amber-500 uppercase tracking-widest mb-1">Skipped</p>
                  <p className="text-2xl font-black text-amber-600">{result.skipped}</p>
                </div>
              </div>

              {result.failed_details?.length > 0 && (
                <div className="max-h-32 overflow-y-auto custom-scrollbar bg-red-50/30 rounded-xl p-3 border border-red-100">
                  <p className="text-[9px] font-black text-red-500 uppercase mb-2">Failure Log</p>
                  {result.failed_details.map((f, i) => (
                    <div key={i} className="text-[10px] font-bold text-red-700 py-1 border-b border-red-100 last:border-0 flex justify-between">
                      <span>{f.mobile}</span>
                      <span className="italic opacity-60">{f.reason}</span>
                    </div>
                  ))}
                </div>
              )}

              <Button className="w-full h-12 rounded-2xl font-black uppercase text-xs tracking-widest" onClick={onClose}>
                Finish & Close
              </Button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ResendBroadcastModal;